import { AxiosInstance } from "axios";

export interface AvailableAE {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  scheduling_link: string;
}

export interface GetAvailableAEsResponseI {
  aes: AvailableAE[];
}

export interface DetectMeetingMultiAERequest {
  contact_email: string;
  ae_ids?: string[];
}

export interface CalendarAttendeeI {
  name: string;
  email: string;
  acceptance_status: string;
  is_organizer: boolean;
}

export interface DetectMeetingMultiAEResponseI {
  found: boolean;
  ae_id?: string;
  meeting_time?: string;
  event_id?: string;
  attendees?: CalendarAttendeeI[];
}

export interface BookMeetingWithTimeRequestI {
  account_executive_id: string;
  meeting_time: string;
  /** GLE-8478: the IANA zone the rep picked the time in. */
  meeting_timezone?: string;
  notes?: string;
  calendar_event_id?: string;
  attendees?: CalendarAttendeeI[];
  address?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  product?: string;
  comments?: string;
  confirmed?: boolean;
}

export interface BookMeetingWithTimeResponseI {
  booked_meeting_id: string;
}

// GLE-8249: move the detected calendar event in place (same event, attendees
// get an "updated" invite) rather than booking a new slot. ISO 8601 instant.
export interface MoveMeetingRequestI {
  meeting_time: string;
  /** GLE-8478: the IANA zone the rep picked the new time in. */
  meeting_timezone?: string;
}

export interface DetectMeetingFromCalendarRequestI {
  contact_email: string;
  ae_id: string;
}

export interface DetectMeetingFromCalendarResponseI {
  found: boolean;
  ae_id?: string;
  meeting_time?: string;
  event_id?: string;
  attendees?: CalendarAttendeeI[];
}

// GLE-8421: notes merged onto one booked meeting by id.
export interface UpdateBookedMeetingNotesRequestI {
  notes: string;
}

export interface UpdateBookedMeetingNotesResponseI {
  activity_log_id: string;
  notes_merge: string;
}

export const SchedulingEndpoints = (client: AxiosInstance) => ({
  // isAttribution: the meeting is already booked and the caller only needs to
  // name who it went to, so the backend skips both the calendar-connection and
  // the scheduling-link filters. The wire param keeps its original name.
  getAvailableAEs: (campaignId: string, isAttribution?: boolean) =>
    client.get<GetAvailableAEsResponseI>(
      `/v1/campaign/${campaignId}/available_aes`,
      isAttribution ? { params: { skip_calendar_check: true } } : undefined
    ),

  detectMeetingMultiAE: (
    campaignId: string,
    accountId: string,
    data: DetectMeetingMultiAERequest
  ) =>
    client.post<DetectMeetingMultiAEResponseI>(
      `/v1/campaign/${campaignId}/account/${accountId}/detect_meeting_multi_ae`,
      data
    ),

  bookMeetingWithTime: (
    campaignId: string,
    accountId: string,
    contactId: string,
    params: BookMeetingWithTimeRequestI
  ) =>
    client.post<BookMeetingWithTimeResponseI>(
      `/v1/campaign/${campaignId}/account/${accountId}/contact/${contactId}/book_meeting_with_time`,
      { ...params }
    ),

  rescheduleMeetingWithTime: (
    campaignId: string,
    accountId: string,
    contactId: string,
    params: BookMeetingWithTimeRequestI
  ) =>
    client.post<BookMeetingWithTimeResponseI>(
      `/v1/campaign/${campaignId}/account/${accountId}/contact/${contactId}/reschedule_meeting_with_time`,
      { ...params }
    ),

  moveMeeting: (
    campaignId: string,
    accountId: string,
    contactId: string,
    params: MoveMeetingRequestI
  ) =>
    client.post<BookMeetingWithTimeResponseI>(
      `/v1/campaign/${campaignId}/account/${accountId}/contact/${contactId}/move_meeting`,
      { ...params }
    ),

  detectMeetingFromCalendar: (
    campaignId: string,
    accountId: string,
    params: DetectMeetingFromCalendarRequestI
  ) =>
    client.post<DetectMeetingFromCalendarResponseI>(
      `/v1/campaign/${campaignId}/account/${accountId}/detect_meeting`,
      { ...params }
    ),

  updateBookedMeetingNotes: (
    campaignId: string,
    accountId: string,
    activityLogId: string,
    params: UpdateBookedMeetingNotesRequestI
  ) =>
    client.patch<UpdateBookedMeetingNotesResponseI>(
      `/v1/campaign/${campaignId}/account/${accountId}/booked_meeting/${activityLogId}/notes`,
      { ...params }
    ),
});
